import { useState, useMemo } from "react";
import {
  Boxes, Plus, Search, Filter, Edit3, Trash2, Copy, Check, Download,
  ArrowUpDown, AlertCircle, ShoppingBag, Layers, IndianRupee,
  RefreshCw, CheckCircle2, ChevronRight, X, ExternalLink, Sparkles,
  Barcode, Tag, Printer, Cloud, CloudCheck, UploadCloud
} from "lucide-react";
import { saveMasterSku, deleteMasterSku, updateMasterSkuStock, importProductsToMasterSkus, syncAllMasterSkusToFirebase } from "../lib/dataStore";
import { HAS_FIREBASE } from "../lib/firebase";
import { toast } from "sonner";
import ProductLabelModal from "./ProductLabelModal";

const DEFAULT_CATEGORIES = ["Leather Bags", "Cotton Bags", "Accessories", "Home & Living"];

export default function MasterSkuPanel({
  masterSkus = [],
  products = [],
  refresh,
  onOpenInvoiceWithSku,
}) {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [stockFilter, setStockFilter] = useState("all"); // 'all' | 'in_stock' | 'low_stock' | 'out_of_stock'
  const [sortBy, setSortBy] = useState("sku_asc"); // 'sku_asc' | 'sku_desc' | 'name_asc' | 'qty_desc' | 'qty_asc' | 'price_desc' | 'cost_desc'
  
  const [editingSku, setEditingSku] = useState(null); // null or sku object (for add/edit modal)
  const [isNewSku, setIsNewSku] = useState(false);
  const [importing, setImporting] = useState(false);
  const [copiedSku, setCopiedSku] = useState(null);

  // Label & Barcode Generator state
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelModalItem, setLabelModalItem] = useState(null);

  // Quick inline stock editing state: { [skuId]: number }
  const [editingStockId, setEditingStockId] = useState(null);
  const [inlineStockVal, setInlineStockVal] = useState("");
  const [syncingCloud, setSyncingCloud] = useState(false);

  // Categories list (defaults + any custom categories in SKUs)
  const categories = useMemo(() => {
    const set = new Set(DEFAULT_CATEGORIES);
    masterSkus.forEach((s) => {
      if (s.category) set.add(s.category);
    });
    return Array.from(set);
  }, [masterSkus]);

  // KPI Calculations
  const stats = useMemo(() => {
    const totalSkus = masterSkus.length;
    let totalQty = 0;
    let totalCostValuation = 0;
    let totalRetailValuation = 0;
    let lowStockCount = 0;
    let outOfStockCount = 0;

    masterSkus.forEach((s) => {
      const q = Number(s.quantity || 0);
      const cost = Number(s.purchaseCost || 0);
      const price = Number(s.saleCost || 0);
      totalQty += q;
      totalCostValuation += q * cost;
      totalRetailValuation += q * price;
      if (q === 0) outOfStockCount++;
      else if (q <= 3) lowStockCount++;
    });

    const projectedProfit = totalRetailValuation - totalCostValuation;
    const marginPct = totalRetailValuation > 0 ? Math.round((projectedProfit / totalRetailValuation) * 100) : 0;

    return {
      totalSkus,
      totalQty,
      totalCostValuation,
      totalRetailValuation,
      projectedProfit,
      marginPct,
      lowStockCount,
      outOfStockCount,
    };
  }, [masterSkus]);

  // Filtered and Sorted Master SKUs
  const filteredSkus = useMemo(() => {
    let result = [...masterSkus];

    // Search query
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (s) =>
          (s.sku || "").toLowerCase().includes(q) ||
          (s.name || "").toLowerCase().includes(q) ||
          (s.category || "").toLowerCase().includes(q) ||
          (s.material || "").toLowerCase().includes(q) ||
          (s.colour || "").toLowerCase().includes(q) ||
          (s.barcode || "").toLowerCase().includes(q)
      );
    }

    // Category filter
    if (selectedCategory !== "all") {
      result = result.filter((s) => s.category === selectedCategory);
    }

    // Stock status filter
    if (stockFilter === "in_stock") {
      result = result.filter((s) => Number(s.quantity || 0) > 0);
    } else if (stockFilter === "low_stock") {
      result = result.filter((s) => {
        const q = Number(s.quantity || 0);
        return q > 0 && q <= 3;
      });
    } else if (stockFilter === "out_of_stock") {
      result = result.filter((s) => Number(s.quantity || 0) === 0);
    }

    // Sorting
    result.sort((a, b) => {
      if (sortBy === "sku_asc") return (a.sku || "").localeCompare(b.sku || "");
      if (sortBy === "sku_desc") return (b.sku || "").localeCompare(a.sku || "");
      if (sortBy === "name_asc") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "qty_desc") return Number(b.quantity || 0) - Number(a.quantity || 0);
      if (sortBy === "qty_asc") return Number(a.quantity || 0) - Number(b.quantity || 0);
      if (sortBy === "price_desc") return Number(b.saleCost || 0) - Number(a.saleCost || 0);
      if (sortBy === "cost_desc") return Number(b.purchaseCost || 0) - Number(a.purchaseCost || 0);
      return 0;
    });

    return result;
  }, [masterSkus, search, selectedCategory, stockFilter, sortBy]);

  // Copy SKU to clipboard
  const handleCopySku = (skuText) => {
    navigator.clipboard.writeText(skuText);
    setCopiedSku(skuText);
    toast.success(`Copied SKU: ${skuText}`);
    setTimeout(() => setCopiedSku(null), 1800);
  };

  // Quick inline stock updater
  const handleSaveInlineStock = async (skuId) => {
    const val = parseInt(inlineStockVal, 10);
    if (isNaN(val) || val < 0) {
      toast.error("Please enter a valid non-negative quantity");
      return;
    }
    await updateMasterSkuStock(skuId, val, true);
    toast.success("Stock quantity updated");
    setEditingStockId(null);
    refresh();
  };

  // Quick adjust +/- stock
  const handleQuickAdjustStock = async (skuId, delta) => {
    await updateMasterSkuStock(skuId, delta, false);
    toast.success(delta > 0 ? "Stock incremented (+1)" : "Stock decremented (-1)");
    refresh();
  };

  // One-click import from store products
  const handleImportFromProducts = async () => {
    if (!products.length) {
      return toast.info("No store products found to import.");
    }
    setImporting(true);
    try {
      const res = await importProductsToMasterSkus(products);
      toast.success(`Import complete: ${res.added} new SKU(s) added, ${res.updated} existing SKU(s) synced!`);
      refresh();
    } catch (err) {
      toast.error(err.message || "Failed to import products to Master SKU list");
    } finally {
      setImporting(false);
    }
  };

  // Export to CSV
  const handleExportCsv = () => {
    if (!masterSkus.length) return toast.info("No Master SKUs to export");
    const headers = [
      "SKU ID",
      "Product Name",
      "Category",
      "Purchase Cost (INR)",
      "Additional Cost (INR)",
      "Sale Cost / Price (INR)",
      "MRP (INR)",
      "Stock Quantity",
      "GST Rate (%)",
      "Material",
      "Colour",
      "Size",
      "Weight",
      "Bag Type",
      "Description",
      "Barcode"
    ];

    const rows = masterSkus.map((s) => [
      `"${s.sku || ""}"`,
      `"${(s.name || "").replace(/"/g, '""')}"`,
      `"${s.category || ""}"`,
      s.purchaseCost ?? 0,
      s.additionalCost ?? 0,
      s.saleCost ?? 0,
      s.mrp ?? 0,
      s.quantity ?? 0,
      s.gstRate ?? 0,
      `"${(s.material || "").replace(/"/g, '""')}"`,
      `"${(s.colour || "").replace(/"/g, '""')}"`,
      `"${(s.size || "").replace(/"/g, '""')}"`,
      `"${(s.weight || "").replace(/"/g, '""')}"`,
      `"${(s.bagType || "").replace(/"/g, '""')}"`,
      `"${(s.description || "").replace(/"/g, '""')}"`,
      `"${s.barcode || ""}"`
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `master_skus_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Master SKU list exported to CSV");
  };

  const handleDelete = async (id, skuCode) => {
    if (!window.confirm(`Are you sure you want to delete Master SKU "${skuCode}"?`)) return;
    try {
      await deleteMasterSku(id);
      toast.success(`Deleted SKU ${skuCode}`);
      refresh();
    } catch (err) {
      toast.error(err.message || "Failed to delete Master SKU");
    }
  };

  const handleCloudSync = async () => {
    setSyncingCloud(true);
    try {
      const res = await syncAllMasterSkusToFirebase();
      toast.success(`Cloud sync complete: ${res.synced} SKU(s) saved to Firebase and accessible everywhere!`);
      refresh();
    } catch (err) {
      toast.error(err.message || "Cloud sync failed");
    } finally {
      setSyncingCloud(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* 1. Header Banner & Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-card border border-border p-6 rounded-2xl shadow-2xs">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="p-2 rounded-lg bg-terracotta/10 text-terracotta">
              <Boxes className="w-5 h-5" />
            </span>
            <span className="overline text-terracotta font-semibold">Central Registry</span>
            {HAS_FIREBASE && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Firebase Cloud Active
              </span>
            )}
          </div>
          <h2 className="font-serif text-2xl md:text-3xl font-bold mt-1 text-foreground">
            Master SKU &amp; Product List
          </h2>
          <p className="text-muted-foreground text-xs md:text-sm mt-1 max-w-2xl">
            Maintain unique custom SKU IDs with exact purchase costs, stock quantities, sale prices, and additional costs.
            Saved in Firebase Cloud to ensure real-time access across all devices and sessions.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {HAS_FIREBASE && (
            <button
              type="button"
              data-testid="sync-cloud-master-sku-btn"
              disabled={syncingCloud}
              onClick={handleCloudSync}
              className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-full border border-border bg-background hover:bg-muted font-medium text-xs sm:text-sm text-foreground transition cursor-pointer shadow-2xs disabled:opacity-50"
              title="Ensure all Master SKUs are uploaded to Firebase Cloud"
            >
              <UploadCloud className={`w-4 h-4 text-terracotta ${syncingCloud ? "animate-bounce" : ""}`} />
              <span>{syncingCloud ? "Syncing…" : "Sync to Cloud"}</span>
            </button>
          )}

          <button
            type="button"
            data-testid="add-master-sku-btn"
            onClick={() => {
              setIsNewSku(true);
              setEditingSku({
                sku: "",
                name: "",
                category: "Leather Bags",
                purchaseCost: "",
                saleCost: "",
                mrp: "",
                quantity: "10",
                additionalCost: "80",
                gstRate: "0",
                shippingCharge: "0",
                material: "",
                colour: "",
                size: "",
                weight: "",
                bagType: "",
                description: "",
              });
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-terracotta text-white font-medium text-xs sm:text-sm hover:opacity-95 shadow-sm transition cursor-pointer"
          >
            <Plus className="w-4 h-4" /> Add Master SKU
          </button>

          {/* Barcode & MRP Label Generator Button */}
          <button
            type="button"
            data-testid="master-sku-label-btn"
            onClick={() => {
              setLabelModalItem(null);
              setShowLabelModal(true);
            }}
            className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-full border border-border bg-background hover:bg-muted font-medium text-xs sm:text-sm text-foreground transition cursor-pointer shadow-2xs"
            title="Generate & print barcode labels with MRP for thermal rolls or A4 sheets"
          >
            <Barcode className="w-4 h-4 text-terracotta" />
            <span>Print Labels &amp; Barcode</span>
          </button>

          {products.length > 0 && (
            <button
              type="button"
              data-testid="import-products-sku-btn"
              disabled={importing}
              onClick={handleImportFromProducts}
              className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-full border border-border bg-background hover:bg-muted font-medium text-xs text-foreground transition cursor-pointer"
              title="Populate master SKUs from current shop products"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-terracotta ${importing ? "animate-spin" : ""}`} />
              <span>Import Store Products</span>
            </button>
          )}

          <button
            type="button"
            data-testid="export-master-skus-btn"
            onClick={handleExportCsv}
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-full border border-border bg-background hover:bg-muted font-medium text-xs text-foreground transition cursor-pointer"
            title="Download CSV report"
          >
            <Download className="w-3.5 h-3.5 text-muted-foreground" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* 2. KPI Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center justify-between">
            <span>Total SKUs</span>
            <Boxes className="w-3.5 h-3.5 text-terracotta" />
          </div>
          <div className="text-2xl font-bold font-price mt-1 text-foreground">
            {stats.totalSkus}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {categories.length} active categories
          </div>
        </div>

        <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center justify-between">
            <span>Total Units Stock</span>
            <Layers className="w-3.5 h-3.5 text-sky-600" />
          </div>
          <div className="text-2xl font-bold font-price mt-1 text-foreground">
            {stats.totalQty}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {stats.lowStockCount > 0 ? (
              <span className="text-amber-600 font-semibold">{stats.lowStockCount} items low stock</span>
            ) : (
              <span className="text-emerald-600">Stock healthy</span>
            )}
          </div>
        </div>

        <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center justify-between">
            <span>Purchase Valuation</span>
            <IndianRupee className="w-3.5 h-3.5 text-amber-600" />
          </div>
          <div className="text-2xl font-bold font-price mt-1 text-foreground">
            ₹{stats.totalCostValuation.toLocaleString("en-IN")}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Invested inventory cost
          </div>
        </div>

        <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center justify-between">
            <span>Retail Valuation</span>
            <IndianRupee className="w-3.5 h-3.5 text-emerald-600" />
          </div>
          <div className="text-2xl font-bold font-price mt-1 text-emerald-700 dark:text-emerald-400">
            ₹{stats.totalRetailValuation.toLocaleString("en-IN")}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Gross potential: ₹{stats.projectedProfit.toLocaleString("en-IN")}
          </div>
        </div>

        <div className="col-span-2 lg:col-span-1 bg-card border border-border p-4 rounded-xl shadow-2xs">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center justify-between">
            <span>Projected Margin</span>
            <Sparkles className="w-3.5 h-3.5 text-terracotta" />
          </div>
          <div className="text-2xl font-bold font-price mt-1 text-foreground">
            {stats.marginPct}%
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Avg gross profit margin
          </div>
        </div>
      </div>

      {/* 3. Search, Filter, Sort Toolbar */}
      <div className="bg-card border border-border p-4 rounded-xl shadow-2xs space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          {/* Search */}
          <div className="md:col-span-5 relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              data-testid="search-master-sku-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by SKU ID (e.g. LC-BAG-001), name, material..."
              className="w-full pl-9 pr-8 py-2 bg-muted/30 border border-border rounded-lg text-xs sm:text-sm outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta transition"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Category Filter */}
          <div className="md:col-span-3">
            <select
              data-testid="category-filter-sku-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full py-2 px-3 bg-muted/30 border border-border rounded-lg text-xs sm:text-sm outline-none focus:border-terracotta"
            >
              <option value="all">All Categories ({masterSkus.length})</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Stock Filter */}
          <div className="md:col-span-2">
            <select
              data-testid="stock-filter-sku-select"
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              className="w-full py-2 px-3 bg-muted/30 border border-border rounded-lg text-xs sm:text-sm outline-none focus:border-terracotta"
            >
              <option value="all">All Stock Status</option>
              <option value="in_stock">In Stock (&gt;0)</option>
              <option value="low_stock">Low Stock (≤3)</option>
              <option value="out_of_stock">Out of Stock (0)</option>
            </select>
          </div>

          {/* Sort By */}
          <div className="md:col-span-2">
            <select
              data-testid="sort-sku-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full py-2 px-3 bg-muted/30 border border-border rounded-lg text-xs sm:text-sm outline-none focus:border-terracotta"
            >
              <option value="sku_asc">SKU ID (A → Z)</option>
              <option value="sku_desc">SKU ID (Z → A)</option>
              <option value="name_asc">Name (A → Z)</option>
              <option value="qty_desc">Stock (High → Low)</option>
              <option value="qty_asc">Stock (Low → High)</option>
              <option value="price_desc">Sale Price (High → Low)</option>
              <option value="cost_desc">Purchase Cost (High → Low)</option>
            </select>
          </div>
        </div>

        {/* Filter Summary Tags */}
        {(search || selectedCategory !== "all" || stockFilter !== "all") && (
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/60 text-xs">
            <span className="text-muted-foreground">Active filters:</span>
            {search && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-foreground border border-border font-mono text-[11px]">
                Search: "{search}"
                <button onClick={() => setSearch("")} className="hover:text-destructive"><X className="w-3 h-3" /></button>
              </span>
            )}
            {selectedCategory !== "all" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-foreground border border-border text-[11px]">
                Category: {selectedCategory}
                <button onClick={() => setSelectedCategory("all")} className="hover:text-destructive"><X className="w-3 h-3" /></button>
              </span>
            )}
            {stockFilter !== "all" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-foreground border border-border text-[11px]">
                Stock: {stockFilter.replace("_", " ")}
                <button onClick={() => setStockFilter("all")} className="hover:text-destructive"><X className="w-3 h-3" /></button>
              </span>
            )}
            <button
              onClick={() => { setSearch(""); setSelectedCategory("all"); setStockFilter("all"); }}
              className="text-[11px] text-terracotta hover:underline ml-2"
            >
              Reset all
            </button>
            <span className="ml-auto text-muted-foreground text-[11px]">
              Showing {filteredSkus.length} of {masterSkus.length} SKUs
            </span>
          </div>
        )}
      </div>

      {/* 4. Master SKU Data Table */}
      <div className="bg-card border border-border rounded-xl shadow-2xs overflow-hidden">
        {filteredSkus.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto text-muted-foreground">
              <Boxes className="w-6 h-6" />
            </div>
            <h3 className="font-serif text-lg font-bold text-foreground">No Master SKUs found</h3>
            <p className="text-xs text-muted-foreground max-w-md mx-auto">
              {masterSkus.length === 0
                ? "You haven't added any products to your Master SKU Catalog yet. Click 'Add Master SKU' or import your existing store products to begin tracking."
                : "No SKUs match your current search or filter criteria."}
            </p>
            {masterSkus.length === 0 && products.length > 0 && (
              <button
                type="button"
                onClick={handleImportFromProducts}
                className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-terracotta text-white text-xs font-semibold hover:opacity-90"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Import {products.length} Products into Master SKU List
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-muted-foreground text-[11px] uppercase tracking-wider font-semibold">
                  <th className="py-3 px-4">Custom SKU ID</th>
                  <th className="py-3 px-4">Product Name &amp; Details</th>
                  <th className="py-3 px-3">Category</th>
                  <th className="py-3 px-3 text-right">Purchase Cost</th>
                  <th className="py-3 px-3 text-right">Additional Cost</th>
                  <th className="py-3 px-3 text-right">Sale Price / MRP</th>
                  <th className="py-3 px-3 text-right">Unit Margin</th>
                  <th className="py-3 px-4 text-center">Stock Quantity</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {filteredSkus.map((item) => {
                  const qty = Number(item.quantity || 0);
                  const purchase = Number(item.purchaseCost || 0);
                  const additional = Number(item.additionalCost || 0);
                  const totalUnitCost = purchase + additional;
                  const salePrice = Number(item.saleCost || 0);
                  const unitMargin = salePrice - totalUnitCost;
                  const marginPct = salePrice > 0 ? Math.round((unitMargin / salePrice) * 100) : 0;
                  const isLow = qty > 0 && qty <= 3;
                  const isOut = qty === 0;

                  return (
                    <tr
                      key={item.id}
                      data-testid={`master-sku-row-${item.sku}`}
                      className="hover:bg-muted/20 transition-colors group"
                    >
                      {/* SKU Badge */}
                      <td className="py-3 px-4 font-mono font-bold text-xs whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span
                            onClick={() => handleCopySku(item.sku)}
                            className="bg-muted px-2 py-1 rounded border border-border text-foreground hover:border-terracotta cursor-pointer inline-flex items-center gap-1 group-hover:bg-background transition"
                            title="Click to copy SKU"
                          >
                            <span>{item.sku}</span>
                            {copiedSku === item.sku ? (
                              <Check className="w-3 h-3 text-emerald-600" />
                            ) : (
                              <Copy className="w-3 h-3 text-muted-foreground opacity-60 group-hover:opacity-100" />
                            )}
                          </span>
                        </div>
                        {(item.hsn || item.hsnCode) && (
                          <div className="text-[10px] text-terracotta font-mono font-semibold mt-0.5">
                            HSN: {item.hsn || item.hsnCode}
                          </div>
                        )}
                        {item.barcode && item.barcode !== item.sku && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            Bar: {item.barcode}
                          </div>
                        )}
                      </td>

                      {/* Product Name & Specs */}
                      <td className="py-3 px-4 max-w-xs">
                        <div className="font-semibold text-foreground line-clamp-1">{item.name}</div>
                        <div className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                          {[item.material, item.colour, item.size, item.weight]
                            .filter(Boolean)
                            .join(" • ") || item.description || "No specs"}
                        </div>
                      </td>

                      {/* Category */}
                      <td className="py-3 px-3 text-muted-foreground text-xs whitespace-nowrap">
                        <span className="px-2 py-0.5 rounded-full bg-muted/60 text-[11px]">
                          {item.category || "General"}
                        </span>
                      </td>

                      {/* Purchase Cost */}
                      <td className="py-3 px-3 text-right font-price font-medium text-foreground whitespace-nowrap">
                        ₹{purchase}
                      </td>

                      {/* Additional Cost */}
                      <td className="py-3 px-3 text-right font-price text-muted-foreground whitespace-nowrap">
                        {additional > 0 ? `+₹${additional}` : "₹0"}
                      </td>

                      {/* Sale Price / MRP */}
                      <td className="py-3 px-3 text-right font-price whitespace-nowrap">
                        <div className="font-bold text-foreground">₹{salePrice}</div>
                        {item.mrp && Number(item.mrp) > salePrice && (
                          <div className="text-[10px] text-muted-foreground line-through">
                            ₹{item.mrp}
                          </div>
                        )}
                      </td>

                      {/* Unit Margin */}
                      <td className="py-3 px-3 text-right font-price whitespace-nowrap">
                        <div className={unitMargin >= 0 ? "font-bold text-emerald-700 dark:text-emerald-400" : "font-bold text-rose-700"}>
                          ₹{unitMargin}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {marginPct}% margin
                        </div>
                      </td>

                      {/* Stock Quantity & Quick Edit */}
                      <td className="py-3 px-4 text-center whitespace-nowrap">
                        {editingStockId === item.id ? (
                          <div className="inline-flex items-center gap-1">
                            <input
                              type="number"
                              min="0"
                              value={inlineStockVal}
                              onChange={(e) => setInlineStockVal(e.target.value)}
                              className="w-16 px-1.5 py-1 text-center font-mono font-bold bg-background border border-terracotta rounded text-xs outline-none"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveInlineStock(item.id);
                                if (e.key === "Escape") setEditingStockId(null);
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleSaveInlineStock(item.id)}
                              className="p-1 bg-terracotta text-white rounded hover:opacity-90 cursor-pointer"
                              title="Save"
                            >
                              <Check className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingStockId(null)}
                              className="p-1 bg-muted text-muted-foreground rounded hover:bg-muted/80 cursor-pointer"
                              title="Cancel"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleQuickAdjustStock(item.id, -1)}
                              disabled={qty <= 0}
                              className="w-5 h-5 rounded flex items-center justify-center bg-muted hover:bg-muted-foreground/20 text-muted-foreground disabled:opacity-30 cursor-pointer text-xs"
                              title="Decrease stock (-1)"
                            >
                              −
                            </button>

                            <span
                              onClick={() => {
                                setEditingStockId(item.id);
                                setInlineStockVal(String(qty));
                              }}
                              className={`px-2 py-0.5 rounded font-mono font-bold text-xs cursor-pointer border ${
                                isOut
                                  ? "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950 dark:text-rose-300"
                                  : isLow
                                  ? "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-300"
                                  : "bg-muted text-foreground border-border hover:border-terracotta"
                              }`}
                              title="Click to edit quantity directly"
                            >
                              {qty} pcs
                            </span>

                            <button
                              type="button"
                              onClick={() => handleQuickAdjustStock(item.id, 1)}
                              className="w-5 h-5 rounded flex items-center justify-center bg-muted hover:bg-muted-foreground/20 text-muted-foreground cursor-pointer text-xs"
                              title="Increase stock (+1)"
                            >
                              +
                            </button>
                          </div>
                        )}

                        <div className="text-[10px] mt-0.5">
                          {isOut ? (
                            <span className="text-rose-600 font-semibold">Out of Stock</span>
                          ) : isLow ? (
                            <span className="text-amber-600 font-semibold">Low Stock</span>
                          ) : (
                            <span className="text-muted-foreground">In Stock</span>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Print Label for this SKU */}
                          <button
                            type="button"
                            data-testid={`sku-label-btn-${item.sku}`}
                            onClick={() => {
                              setLabelModalItem(item);
                              setShowLabelModal(true);
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-muted hover:bg-muted-foreground/20 text-foreground font-medium text-xs transition cursor-pointer"
                            title="Generate & print Barcode sticker with MRP"
                          >
                            <Barcode className="w-3 h-3 text-terracotta" />
                            <span>Label</span>
                          </button>

                          {/* Create Invoice with this SKU */}
                          {onOpenInvoiceWithSku && (
                            <button
                              type="button"
                              data-testid={`sku-invoice-btn-${item.sku}`}
                              onClick={() => onOpenInvoiceWithSku(item)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-terracotta/10 text-terracotta hover:bg-terracotta hover:text-white font-medium text-xs transition cursor-pointer"
                              title="Create Offline Invoice with this SKU"
                            >
                              <ShoppingBag className="w-3 h-3" />
                              <span>Invoice</span>
                            </button>
                          )}

                          {/* Edit SKU */}
                          <button
                            type="button"
                            data-testid={`edit-master-sku-${item.sku}`}
                            onClick={() => {
                              setIsNewSku(false);
                              setEditingSku({ ...item });
                            }}
                            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition cursor-pointer"
                            title="Edit Master SKU details"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>

                          {/* Delete SKU */}
                          <button
                            type="button"
                            data-testid={`delete-master-sku-${item.sku}`}
                            onClick={() => handleDelete(item.id, item.sku)}
                            className="p-1.5 rounded hover:bg-rose-100 hover:text-destructive text-muted-foreground transition cursor-pointer"
                            title="Delete Master SKU"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 5. Add / Edit Master SKU Modal */}
      {editingSku && (
        <MasterSkuEditorModal
          initial={editingSku}
          isNew={isNewSku}
          existingSkus={masterSkus}
          onClose={() => setEditingSku(null)}
          onSaved={() => {
            setEditingSku(null);
            refresh();
          }}
          onOpenLabelModal={(item) => {
            setLabelModalItem(item);
            setShowLabelModal(true);
          }}
        />
      )}

      {/* 6. Product Label & Barcode Generator Modal */}
      <ProductLabelModal
        isOpen={showLabelModal}
        onClose={() => setShowLabelModal(false)}
        initialItem={labelModalItem}
        masterSkus={masterSkus}
        products={products}
      />
    </div>
  );
}

// ============================================================================
// MODAL: ADD / EDIT MASTER SKU
// ============================================================================
function MasterSkuEditorModal({ initial, isNew, existingSkus = [], onClose, onSaved, onOpenLabelModal }) {
  const [form, setForm] = useState({ ...initial });
  const [busy, setBusy] = useState(false);
  const [priceGstMode, setPriceGstMode] = useState(
    initial?.gstMode || (initial?.priceIncludesGst !== false ? "inclusive" : "exclusive")
  );

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  // Auto SKU generator helper
  const handleAutoGenerateSku = () => {
    const catCode = (form.category || "BAG").replace(/[^a-zA-Z]/g, "").slice(0, 3).toUpperCase();
    const randNum = Math.floor(100 + Math.random() * 900);
    const newSku = `LC-${catCode}-${randNum}`;
    setForm((prev) => ({ ...prev, sku: newSku, barcode: prev.barcode || newSku }));
    toast.info(`Generated unique SKU ID: ${newSku}`);
  };

  // Real-time calculations with GST Include/Exclude toggle
  const purchaseCostNum = Number(form.purchaseCost || 0);
  const additionalCostNum = Number(form.additionalCost || 0);
  const totalCost = purchaseCostNum + additionalCostNum;
  const saleCostNum = Number(form.saleCost || 0);
  const gstRateNum = Number(form.gstRate || 0);
  const isGstInclusive = priceGstMode === "inclusive";

  let taxableBasePrice = saleCostNum;
  let gstAmount = 0;
  let finalConsumerPrice = saleCostNum;

  if (gstRateNum > 0 && saleCostNum > 0) {
    if (isGstInclusive) {
      taxableBasePrice = Number((saleCostNum / (1 + gstRateNum / 100)).toFixed(2));
      gstAmount = Number((saleCostNum - taxableBasePrice).toFixed(2));
      finalConsumerPrice = saleCostNum;
    } else {
      taxableBasePrice = saleCostNum;
      gstAmount = Number(((saleCostNum * gstRateNum) / 100).toFixed(2));
      finalConsumerPrice = Number((saleCostNum + gstAmount).toFixed(2));
    }
  }

  // Net revenue (excluding GST) for profit margin
  const netRevenue = isGstInclusive ? taxableBasePrice : saleCostNum;
  const unitProfit = netRevenue - totalCost;
  const marginPct = netRevenue > 0 ? Math.round((unitProfit / netRevenue) * 100) : 0;
  const batchCostValuation = purchaseCostNum * Number(form.quantity || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanSku = (form.sku || "").trim().toUpperCase();
    if (!cleanSku) {
      return toast.error("Unique Custom SKU ID is required (e.g. LC-BAG-001)");
    }
    if (!form.name.trim()) {
      return toast.error("Product name is required");
    }
    if (form.purchaseCost === "" || isNaN(Number(form.purchaseCost))) {
      return toast.error("Purchase cost is required");
    }
    if (form.saleCost === "" || isNaN(Number(form.saleCost))) {
      return toast.error("Sale cost / selling price is required");
    }
    const cleanHsn = (form.hsn || form.hsnCode || "").trim();
    if (!cleanHsn) {
      return toast.error("HSN code is mandatory (e.g. 4202)");
    }

    // Check duplicate SKU if new or renamed
    if (isNew || cleanSku !== initial.sku) {
      const exists = existingSkus.some((s) => s.id !== form.id && s.sku.toUpperCase() === cleanSku);
      if (exists) {
        return toast.error(`SKU "${cleanSku}" already exists! Please use a unique SKU ID.`);
      }
    }

    setBusy(true);
    try {
      await saveMasterSku({
        ...form,
        sku: cleanSku,
        name: form.name.trim(),
        hsn: cleanHsn,
        hsnCode: cleanHsn,
        purchaseCost: Number(form.purchaseCost),
        saleCost: Number(form.saleCost),
        mrp: form.mrp !== "" ? Number(form.mrp) : Math.round(Number(form.saleCost) * 1.4),
        quantity: Math.max(0, Number(form.quantity || 0)),
        additionalCost: Number(form.additionalCost || 0),
        gstRate: Number(form.gstRate || 0),
        gstMode: priceGstMode,
        priceIncludesGst: isGstInclusive,
        shippingCharge: Number(form.shippingCharge || 0),
        barcode: form.barcode?.trim() || cleanSku,
      });

      toast.success(isNew ? `Created Master SKU ${cleanSku}` : `Updated Master SKU ${cleanSku}`);
      onSaved();
    } catch (err) {
      let msg = err.message || "Failed to save Master SKU";
      try {
        const parsed = JSON.parse(msg);
        if (parsed.error) msg = `Firestore error: ${parsed.error}`;
      } catch (parseErr) {
        // Leave msg as err.message if not JSON
        msg = err.message || "Failed to save Master SKU";
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-background w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border border-border shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-background/95 backdrop-blur-xs z-10">
          <div>
            <div className="text-[11px] font-semibold text-terracotta uppercase tracking-wider">
              {isNew ? "New Product Registry" : "Edit SKU Configuration"}
            </div>
            <h3 className="font-serif text-2xl font-bold text-foreground">
              {isNew ? "Add Master SKU" : `Edit ${form.sku}`}
            </h3>
          </div>
          <button
            type="button"
            data-testid="close-master-sku-modal-btn"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* SKU Code & Auto Generator */}
          <div className="bg-terracotta/5 border border-terracotta/20 rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wider text-terracotta">
                Unique Custom SKU ID *
              </label>
              <button
                type="button"
                onClick={handleAutoGenerateSku}
                className="text-[11px] text-terracotta hover:underline font-semibold flex items-center gap-1 cursor-pointer"
              >
                <Sparkles className="w-3 h-3" /> Auto Generate SKU
              </button>
            </div>
            <div className="flex gap-2">
              <input
                data-testid="sku-input"
                type="text"
                required
                value={form.sku}
                onChange={(e) => setForm((p) => ({ ...p, sku: e.target.value.toUpperCase() }))}
                placeholder="e.g. LC-BAG-101, TOTE-BLK-01"
                className="flex-1 bg-background border border-border px-3.5 py-2 rounded-lg font-mono font-bold text-sm tracking-wider uppercase outline-none focus:border-terracotta"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              This unique identifier will be used to quickly select or scan this product during product listing and invoice generation.
            </p>
          </div>

          {/* Product Name & Category */}
          <div className="grid sm:grid-cols-3 gap-4">
            <label className="block sm:col-span-2">
              <span className="block text-xs font-semibold mb-1 text-foreground">Product Title / Name *</span>
              <input
                data-testid="sku-name-input"
                type="text"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="e.g. Handcrafted Santiniketan Leather Classic Tote"
                className="w-full bg-muted/40 border border-border px-3 py-2 rounded-lg text-sm outline-none focus:border-terracotta"
              />
            </label>

            <label className="block">
              <span className="block text-xs font-semibold mb-1 text-foreground">Category</span>
              <input
                data-testid="sku-category-input"
                type="text"
                value={form.category}
                onChange={set("category")}
                placeholder="e.g. Leather Bags"
                list="sku-category-options"
                className="w-full bg-muted/40 border border-border px-3 py-2 rounded-lg text-sm outline-none focus:border-terracotta"
              />
              <datalist id="sku-category-options">
                {DEFAULT_CATEGORIES.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </label>
          </div>

          {/* Financials & Stock Grid */}
          <div className="bg-card border border-border rounded-xl p-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Pricing, GST &amp; Stock
                </span>
                <IndianRupee className="w-3.5 h-3.5 text-emerald-600" />
              </div>

              {/* GST Toggle: Include GST vs Exclude GST */}
              <div className="flex items-center bg-muted p-1 rounded-lg border border-border text-xs">
                <span className="text-[11px] font-semibold text-muted-foreground px-1.5 hidden sm:inline">GST:</span>
                <button
                  type="button"
                  data-testid="sku-toggle-gst-inclusive"
                  onClick={() => setPriceGstMode("inclusive")}
                  className={`px-3 py-1 rounded-md text-xs font-semibold transition ${
                    isGstInclusive
                      ? "bg-background shadow-xs text-emerald-700 dark:text-emerald-400 font-bold"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  title="Entered sale price already includes GST (Retail / MRP)"
                >
                  Include GST
                </button>
                <button
                  type="button"
                  data-testid="sku-toggle-gst-exclusive"
                  onClick={() => setPriceGstMode("exclusive")}
                  className={`px-3 py-1 rounded-md text-xs font-semibold transition ${
                    !isGstInclusive
                      ? "bg-background shadow-xs text-blue-700 dark:text-blue-400 font-bold"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  title="Entered sale price is base taxable price (GST added on top)"
                >
                  Exclude GST
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <label className="block">
                <span className="block text-xs font-medium mb-1 text-foreground">
                  Purchase Cost (₹) *
                </span>
                <input
                  data-testid="sku-purchase-cost"
                  type="number"
                  min="0"
                  required
                  value={form.purchaseCost}
                  onChange={set("purchaseCost")}
                  placeholder="e.g. 450"
                  className="w-full bg-background border border-border px-3 py-2 rounded-lg font-mono font-semibold text-sm outline-none focus:border-terracotta"
                />
              </label>

              <label className="block">
                <span className="block text-xs font-medium mb-1 text-foreground">
                  Additional Cost (₹)
                </span>
                <input
                  data-testid="sku-additional-cost"
                  type="number"
                  min="0"
                  value={form.additionalCost}
                  onChange={set("additionalCost")}
                  placeholder="e.g. 80"
                  className="w-full bg-background border border-border px-3 py-2 rounded-lg font-mono text-sm outline-none focus:border-terracotta"
                />
                <span className="text-[10px] text-muted-foreground block mt-0.5">Pack/Logistics</span>
              </label>

              <label className="block">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-foreground">
                    {isGstInclusive ? "Sale Price (Incl. GST) *" : "Base Price (Excl. GST) *"}
                  </span>
                </div>
                <input
                  data-testid="sku-sale-cost"
                  type="number"
                  min="0"
                  required
                  value={form.saleCost}
                  onChange={set("saleCost")}
                  placeholder="e.g. 999"
                  className="w-full bg-background border border-border px-3 py-2 rounded-lg font-mono font-bold text-emerald-700 dark:text-emerald-400 text-sm outline-none focus:border-terracotta"
                />
              </label>

              <label className="block">
                <span className="block text-xs font-medium mb-1 text-foreground">
                  MRP / Sticker ₹
                </span>
                <input
                  data-testid="sku-mrp"
                  type="number"
                  min="0"
                  value={form.mrp}
                  onChange={set("mrp")}
                  placeholder="e.g. 1499"
                  className="w-full bg-background border border-border px-3 py-2 rounded-lg font-mono text-sm outline-none focus:border-terracotta"
                />
              </label>
            </div>

            {/* Live GST Calculation Banner */}
            <div className="p-3 bg-muted/40 border border-border/80 rounded-lg text-xs space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground">
                    GST Calculation ({gstRateNum}%):
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                    isGstInclusive
                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                      : "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300"
                  }`}>
                    {isGstInclusive ? "Price is GST-Inclusive" : "Price is GST-Exclusive"}
                  </span>
                </div>
                {gstRateNum > 0 && saleCostNum > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (isGstInclusive) {
                        setForm((prev) => ({ ...prev, saleCost: String(taxableBasePrice) }));
                        setPriceGstMode("exclusive");
                        toast.info(`Switched to Exclude GST base price ₹${taxableBasePrice}`);
                      } else {
                        setForm((prev) => ({ ...prev, saleCost: String(finalConsumerPrice) }));
                        setPriceGstMode("inclusive");
                        toast.info(`Switched to Include GST price ₹${finalConsumerPrice}`);
                      }
                    }}
                    className="text-[11px] text-terracotta hover:underline font-medium cursor-pointer"
                  >
                    {isGstInclusive
                      ? `Convert to Exclude GST (Base ₹${taxableBasePrice})`
                      : `Convert to Include GST (Total ₹${finalConsumerPrice})`}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 pt-0.5 font-mono text-[11px]">
                <div className="bg-background p-2 rounded border border-border">
                  <span className="text-[10px] text-muted-foreground block">Taxable Base</span>
                  <strong className="text-foreground">₹{taxableBasePrice}</strong>
                </div>
                <div className="bg-background p-2 rounded border border-border">
                  <span className="text-[10px] text-muted-foreground block">GST Amount ({gstRateNum}%)</span>
                  <strong className="text-amber-700 dark:text-amber-400 font-bold">
                    +₹{gstAmount}
                  </strong>
                </div>
                <div className="bg-background p-2 rounded border border-border">
                  <span className="text-[10px] text-muted-foreground block">Final Selling Price</span>
                  <strong className="text-emerald-700 dark:text-emerald-400 font-bold">
                    ₹{finalConsumerPrice}
                  </strong>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-border/60">
              <label className="block">
                <span className="block text-xs font-semibold mb-1 text-foreground">
                  HSN Code * <span className="text-terracotta text-[10px]">(Mandatory)</span>
                </span>
                <input
                  data-testid="sku-hsn"
                  type="text"
                  required
                  value={form.hsn || form.hsnCode || ""}
                  onChange={(e) => setForm((p) => ({ ...p, hsn: e.target.value.trim(), hsnCode: e.target.value.trim() }))}
                  placeholder="e.g. 4202"
                  className="w-full bg-background border border-border px-3 py-2 rounded-lg font-mono font-semibold text-sm outline-none focus:border-terracotta"
                />
              </label>

              <label className="block">
                <span className="block text-xs font-medium mb-1 text-foreground">
                  Stock Quantity (Pcs) *
                </span>
                <input
                  data-testid="sku-quantity"
                  type="number"
                  min="0"
                  required
                  value={form.quantity}
                  onChange={set("quantity")}
                  placeholder="e.g. 15"
                  className="w-full bg-background border border-border px-3 py-2 rounded-lg font-mono font-bold text-sm outline-none focus:border-terracotta"
                />
              </label>

              <label className="block">
                <span className="block text-xs font-medium mb-1 text-foreground">GST Rate (%)</span>
                <select
                  data-testid="sku-gst-rate"
                  value={form.gstRate}
                  onChange={set("gstRate")}
                  className="w-full bg-background border border-border px-3 py-2 rounded-lg text-sm outline-none focus:border-terracotta"
                >
                  <option value="0">0% GST</option>
                  <option value="5">5% GST</option>
                  <option value="12">12% GST</option>
                  <option value="18">18% GST</option>
                </select>
              </label>

              <label className="block">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-foreground">Barcode / UPC</span>
                  {form.sku && onOpenLabelModal && (
                    <button
                      type="button"
                      data-testid="sku-modal-label-shortcut"
                      onClick={() =>
                        onOpenLabelModal({
                          ...form,
                          id: form.id || form.sku,
                        })
                      }
                      className="text-[11px] text-terracotta hover:underline inline-flex items-center gap-1 font-medium cursor-pointer"
                      title="Open Label Generator with this item"
                    >
                      <Barcode className="w-3 h-3" />
                      <span>Print Label</span>
                    </button>
                  )}
                </div>
                <input
                  data-testid="sku-barcode"
                  type="text"
                  value={form.barcode}
                  onChange={set("barcode")}
                  placeholder={form.sku || "Barcode"}
                  className="w-full bg-background border border-border px-3 py-2 rounded-lg font-mono text-sm outline-none focus:border-terracotta"
                />
              </label>
            </div>

            {/* Live Profit Preview */}
            <div className="p-3 bg-muted/40 rounded-lg text-xs grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <span className="text-[10px] uppercase text-muted-foreground block">Total Unit Cost</span>
                <span className="font-price font-bold text-foreground">₹{totalCost}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase text-muted-foreground block">Unit Profit</span>
                <span className={`font-price font-bold ${unitProfit >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700"}`}>
                  ₹{unitProfit}
                </span>
              </div>
              <div>
                <span className="text-[10px] uppercase text-muted-foreground block">Profit Margin</span>
                <span className="font-price font-bold text-foreground">{marginPct}%</span>
              </div>
              <div>
                <span className="text-[10px] uppercase text-muted-foreground block">Batch Cost Value</span>
                <span className="font-price font-bold text-foreground">₹{batchCostValuation}</span>
              </div>
            </div>
          </div>

          {/* Product Specifications & Details */}
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Product Specifications &amp; Attributes
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block">
                <span className="block text-xs font-medium mb-1 text-foreground">Material</span>
                <input
                  data-testid="sku-material"
                  type="text"
                  value={form.material}
                  onChange={set("material")}
                  placeholder="e.g. Pure Leather, Handloom Cotton"
                  className="w-full bg-background border border-border px-3 py-2 rounded-lg text-xs sm:text-sm outline-none focus:border-terracotta"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium mb-1 text-foreground">Colour / Finish</span>
                <input
                  data-testid="sku-colour"
                  type="text"
                  value={form.colour}
                  onChange={set("colour")}
                  placeholder="e.g. Dark Tan, Natural Brown"
                  className="w-full bg-background border border-border px-3 py-2 rounded-lg text-xs sm:text-sm outline-none focus:border-terracotta"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium mb-1 text-foreground">Bag Type</span>
                <input
                  data-testid="sku-bag-type"
                  type="text"
                  value={form.bagType}
                  onChange={set("bagType")}
                  placeholder="e.g. Tote, Sling, Duffel"
                  className="w-full bg-background border border-border px-3 py-2 rounded-lg text-xs sm:text-sm outline-none focus:border-terracotta"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium mb-1 text-foreground">Size / Dimensions</span>
                <input
                  data-testid="sku-size"
                  type="text"
                  value={form.size}
                  onChange={set("size")}
                  placeholder='e.g. 14" H × 12" W × 4" D'
                  className="w-full bg-background border border-border px-3 py-2 rounded-lg text-xs sm:text-sm outline-none focus:border-terracotta"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium mb-1 text-foreground">Weight</span>
                <input
                  data-testid="sku-weight"
                  type="text"
                  value={form.weight}
                  onChange={set("weight")}
                  placeholder="e.g. 450g / 0.45 kg"
                  className="w-full bg-background border border-border px-3 py-2 rounded-lg text-xs sm:text-sm outline-none focus:border-terracotta"
                />
              </label>
            </div>

            <label className="block">
              <span className="block text-xs font-medium mb-1 text-foreground">Product Description / Notes</span>
              <textarea
                data-testid="sku-description"
                rows={2}
                value={form.description}
                onChange={set("description")}
                placeholder="Key craft details, inner compartments, stitching notes..."
                className="w-full bg-background border border-border px-3 py-2 rounded-lg text-xs sm:text-sm outline-none focus:border-terracotta"
              />
            </label>
          </div>

          {/* Modal Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-full border border-border text-sm hover:bg-muted cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="save-master-sku-btn"
              disabled={busy}
              className="px-6 py-2.5 rounded-full bg-terracotta text-white font-medium text-sm hover:opacity-90 disabled:opacity-50 transition cursor-pointer shadow-sm"
            >
              {busy ? "Saving SKU…" : isNew ? "Save Master SKU" : "Update SKU Details"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
